import { getAuthUser } from '../services/auth';

const SCOPE_PREFIX = 'user_setting';
const MIGRATED_FLAG_KEY = 'user_setting:migration_done';

function getScope(): string {
  if (typeof window === 'undefined') return 'anonymous';
  const user = getAuthUser();
  const userId = user?.id?.trim();
  if (!userId) return 'anonymous';
  const orgId = user.organizationId?.trim() || 'personal';
  return `${userId}:${orgId}`;
}

function scopedKey(baseKey: string): string {
  return `${SCOPE_PREFIX}:${getScope()}:${baseKey}`;
}

export function getItem(baseKey: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(scopedKey(baseKey));
}

export function setItem(baseKey: string, value: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(scopedKey(baseKey), value);
}

export function removeItem(baseKey: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(scopedKey(baseKey));
}

/** Storage interface compatible with zustand persist middleware */
export const scopedStorage = {
  getItem: (key: string) => getItem(key),
  setItem: (key: string, value: string) => setItem(key, value),
  removeItem: (key: string) => removeItem(key),
};

const MIGRATABLE_BASE_KEYS = [
  'ui_theme', 'ui_language', 'ui_language_source',
  'ai_provider', 'ai_model', 'thinking_ai_model', 'routing_mode',
  'custom_instructions', 'knowledge_scope', 'strict_safety_check',
  'custom_model_id', 'custom_model_label', 'custom_model_provider_label',
  'custom_model_provider_name_history',
  'custom_model_connections', 'custom_model_selection_id',
  'custom_model_entries', 'custom_model_migrated',
  'plc_brand', 'plc_language', 'plc_target_profile', 'software_version', 'plc_model',
  'realplc-settings',
];

export function migrateToScoped(): void {
  if (typeof localStorage === 'undefined') return;
  const scope = getScope();
  if (scope === 'anonymous') return;

  if (localStorage.getItem(MIGRATED_FLAG_KEY) === scope) return;

  for (const baseKey of MIGRATABLE_BASE_KEYS) {
    const unscoped = localStorage.getItem(baseKey);
    const scoped = localStorage.getItem(scopedKey(baseKey));
    if (unscoped !== null && scoped === null) {
      localStorage.setItem(scopedKey(baseKey), unscoped);
    }
  }

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (key.endsWith('_api_key') || key.endsWith('_base_url')
      || key === 'qwen_api_key' || key === 'qwen_base_url') {
      const unscoped = localStorage.getItem(key);
      const scoped = localStorage.getItem(scopedKey(key));
      if (unscoped !== null && scoped === null) {
        localStorage.setItem(scopedKey(key), unscoped);
      }
    }
  }

  localStorage.setItem(MIGRATED_FLAG_KEY, scope);
}

export function clearScopedSettings(): void {
  if (typeof localStorage === 'undefined') return;
  const scope = getScope();
  if (scope === 'anonymous') return;

  const prefix = `${SCOPE_PREFIX}:${scope}:`;
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix)) keys.push(key);
  }
  keys.forEach((key) => localStorage.removeItem(key));
}
